import { AlertTriangle, Info } from "lucide-react";
import { useCallback, useRef, useState } from "react";

/**
 * Generic confirm/cancel dialog matching the app's Kinetic Glass style.
 *
 * Two visual variants — `danger` (red accent, for destructive actions like
 * delete) and `info` (neutral, for soft confirmations). The component is
 * controlled: open state + handlers live with the caller, usually via the
 * `useConfirm()` hook below which returns a promise-based `confirm()` fn.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Bestätigen",
  cancelLabel = "Abbrechen",
  variant = "danger",
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "info";
  /** Show a busy indicator on the confirm button (e.g. mutation pending). */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  const Icon = variant === "danger" ? AlertTriangle : Info;
  const iconWrap =
    variant === "danger" ? "bg-rose-500/15 text-rose-300" : "bg-sky-500/15 text-sky-300";
  const confirmBtn =
    variant === "danger"
      ? "bg-rose-500 text-white hover:bg-rose-400"
      : "bg-pitch-lime text-[#1d3700] hover:brightness-110 shadow-glow";
  return (
    <div
      className="fixed inset-0 z-300 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
        if (e.key === "Enter") onConfirm();
      }}
      role="presentation"
    >
      <div
        className="glass-floating w-full max-w-md rounded-3xl border border-white/10 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className="flex items-start gap-3">
          <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl ${iconWrap}`}>
            <Icon className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="confirm-dialog-title" className="text-body-md font-semibold leading-tight">
              {title}
            </h2>
            <p className="mt-1 whitespace-pre-line text-body-sm text-muted-foreground">{message}</p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-10 items-center justify-center rounded-full bg-white/6 px-4 text-body-sm hover:bg-white/10"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex h-10 items-center justify-center rounded-full px-4 text-body-sm font-semibold disabled:opacity-60 ${confirmBtn}`}
            autoFocus
          >
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook flavour — call `confirm({title,message})` and await a boolean.
 * Render `<dialog />` once anywhere in the tree. Closely mirrors the native
 * `window.confirm` shape so migrating existing call sites is a one-liner.
 *
 * ```tsx
 * const { confirm, dialog } = useConfirm();
 * // ...
 * const ok = await confirm({ title: "Löschen?", message: "…" });
 * if (ok) deleteIt();
 * return <>{children}{dialog}</>;
 * ```
 */
export function useConfirm() {
  const [state, setState] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: "danger" | "info";
  }>({ open: false, title: "", message: "" });

  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback(
    (opts: {
      title: string;
      message: string;
      confirmLabel?: string;
      cancelLabel?: string;
      variant?: "danger" | "info";
    }) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setState({ ...opts, open: true });
      }),
    [],
  );

  const close = useCallback((value: boolean) => {
    setState((s) => ({ ...s, open: false }));
    resolverRef.current?.(value);
    resolverRef.current = null;
  }, []);

  const dialog = (
    <ConfirmDialog
      open={state.open}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      variant={state.variant}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );

  return { confirm, dialog };
}
